import React, { useCallback, useEffect, useState } from "react";
import { Form, useFormikContext } from "formik";
import Textarea from "@adsk/alloy-react-textarea";
import TextInput from "@adsk/alloy-react-text-input";
import {
  FieldMetadataForTypes as FieldMetadata,
  GpsCoordinates,
  IssueAttributes,
  IssueStandardFieldIds,
  IssueStatus,
  IssueType,
  PermittedAction,
} from "@adsk/acc-issues-types";
import { FieldWrapper } from "@adsk/acc-issues-common/components/primitive/FieldWrapper";
import { StatusDropdown } from "@adsk/acc-issues-common/components/input/StatusDropdown";
import { TypeDropdown } from "@adsk/acc-issues-common/components/input/TypeDropdown";
import { RootCauseDropdown } from "@adsk/acc-issues-common/components/input/RootCauseDropdown";
import { WatcherDropdown } from "@adsk/acc-issues-common/components/input/WatcherDropdown";
import { WatcherFieldLabel } from "@adsk/acc-issues-common/components/display/WatcherFieldLabel";
import { AssigneeDropdown } from "@adsk/acc-issues-common/components/input/ProjectMemberDropdown/AssigneeDropdown";
import { DateField } from "@adsk/acc-issues-common/components/input/DateField";
import { CustomFieldWithFormikContext } from "../input-field-components/CustomFieldWithFormikContext";
import { useGetLevelOneUserShouldIncludeMemberFn } from "@adsk/acc-issues-common/hooks/data/useGetLevelOneUserShouldIncludeMemberFn";
import PlacementDropdown from "../input-field-components/PlacementDropdown/PlacementDropdown";
import { IssueTemplatesField } from "./IssueTemplatesField";
import { FixedTypeField } from "./FixedTypeField";
import { LocationCreateField } from "./LocationCreateField";
import { useLocalStorage } from "@adsk/acc-issues-core/utils/hooks/useLocalStorage";
import { LocalStorageKeys } from "@adsk/acc-issues-core/state/localStorage";
import { useSelector } from "@adsk/acc-issues-core/state/redux/hooks";
import {
  getCurrentUserPermittedActionsForNewIssue,
  getCurrentUserPermittedStatusesForNewIssue,
} from "@adsk/acc-issues-core/state/redux/ducks/currentUserPermissions";
import { getIssueTypeEntitiesMap } from "@adsk/acc-issues-core/state/redux/ducks/issueTypes";
import { ConditionalTooltip } from "@adsk/acc-issues-common/components/primitive/ConditionalTooltip";
import { useHydratedRef } from "@adsk/acc-issues-common/hooks/dom/useHydratedRef";
import {
  useCustomFieldIdsWithMappingsForType,
  useGetCustomFieldMappingsByIssueTypeId,
} from "../utils/hooks/issueCustomFieldMappingHooks";
import { useScrollToFirstErrorOnSubmit } from "../utils/hooks/useScrollToFirstErrorOnSubmit";
import { useScrollToAttachmentErrorInCreationForm } from "../utils/hooks/useScrollToAttachmentErrorInCreationForm";
import { useTranslations } from "@adsk/acc-issues-translations";
import { useChangeStatusWhenRequiredFieldsAreHidden } from "../utils/hooks/requiredFieldsByTypeHooks";
import { useGetSanitizedFieldOrder } from "../utils/data/issueFieldOrdering";
import { IssueFormAttributes } from "@adsk/acc-issues-core/utils/types/Issue";
import { useIncrementor } from "../utils/hooks/useIncrementor";
import {
  FeatureFlag,
  useFeatureFlag,
} from "@adsk/acc-issues-core/utils/featureFlags";
import { GeoLocationPickerField } from "../input-field-components/GeoLocationPickerField";
import { useWatcherPropName } from "../utils/hooks/watchers/useWatcherPropName";
import { WatcherObject } from "@adsk/acc-issues-types";
import { useGetWatchersValue } from "../utils/hooks/watchers/useGetWatchersValue";
import { AttachmentsField } from "../input-field-components/AttachmentsField";
import { useMaybeShowWatchersNotification } from "@adsk/acc-issues-common/hooks/notifications/useMaybeShowWatchersNotification";
import { useInitialType } from "./useInitialType";

export interface IssueCreationFormProps {
  titleFieldLabel?: string;
  requireTitleField?: boolean;
  hideStatusField?: boolean;
  hiddenStandardFields?: Array<keyof IssueStandardFieldIds>;
  fixedTypeId?: string;
}

export const IssueCreationForm = ({
  titleFieldLabel,
  requireTitleField = true,
  hideStatusField,
  hiddenStandardFields,
  fixedTypeId = undefined,
}: IssueCreationFormProps) => {
  const {
    values,
    handleChange,
    handleBlur,
    setFieldValue,
    setFieldTouched,
    touched,
    errors,
    submitCount,
  } = useFormikContext<IssueFormAttributes>();

  const typeEntities = useSelector(getIssueTypeEntitiesMap);
  const selectedType: IssueType | undefined =
    typeEntities?.[values.issueTypeId];

  const { mappingsByCustomFieldId, customFieldIds } =
    useCustomFieldIdsWithMappingsForType(values.issueTypeId);
  const getCustomFieldMappingsByIssueTypeId =
    useGetCustomFieldMappingsByIssueTypeId();

  const { value: shouldShowAttachments } = useFeatureFlag(
    FeatureFlag.Attachments1_1
  );

  const titleInputRef = React.useRef<HTMLInputElement>(null);
  useEffect(() => {
    titleInputRef.current?.select();
  }, []);

  const issueFields = useGetSanitizedFieldOrder({
    fieldList: selectedType?.fieldsMetadata || [],
    customFieldIdsForType: customFieldIds,
    shouldShowAttachments,
  })
    // Hide any fields that the consumer has explicitly requested to be hidden
    .map((field: FieldMetadata) =>
      hiddenStandardFields?.some(hiddenField => hiddenField === field.id)
        ? Object.assign({ ...field }, { visible: false })
        : field
    );

  const requiredStandardFieldIds = (selectedType?.fieldsMetadata || [])
    .filter(({ required, type }) => type === "standard" && !!required)
    .map(({ id }) => id);
  const areThereHiddenRequiredStandardFields =
    Array.isArray(hiddenStandardFields) &&
    hiddenStandardFields.some(f => {
      return requiredStandardFieldIds.includes(f as string);
    });

  useChangeStatusWhenRequiredFieldsAreHidden({
    hideStatusField,
    areThereHiddenRequiredStandardFields,
    issueFormValues: values,
    selectedType,
    setFieldValue,
  });

  const [, setLastSelectedTypeId] = useLocalStorage(
    LocalStorageKeys.LAST_SELECTED_TYPE_ID
  );
  const permittedStatuses = useSelector(
    getCurrentUserPermittedStatusesForNewIssue
  );
  const permittedActions = useSelector(
    getCurrentUserPermittedActionsForNewIssue
  );

  const levelOneUserShouldIncludeMemberFn =
    useGetLevelOneUserShouldIncludeMemberFn();
  const shouldIncludeMemberFn = !permittedActions.includes(
    PermittedAction.AssignAll
  )
    ? levelOneUserShouldIncludeMemberFn
    : undefined;

  const clearObsoleteCustomFields = useCallback(
    (newTypeId: string) => {
      const allowedCustomFieldIds = getCustomFieldMappingsByIssueTypeId(
        newTypeId
      ).map(m => m.attributeDefinitionId);
      const allowedSet = new Set(allowedCustomFieldIds);
      const filteredCustomAttributes = Object.fromEntries(
        Object.entries(values.customAttributes ?? {}).filter(([id]) =>
          allowedSet.has(id)
        )
      );
      setFieldValue("customAttributes", filteredCustomAttributes);
    },
    [
      getCustomFieldMappingsByIssueTypeId,
      setFieldValue,
      values.customAttributes,
    ]
  );

  const ref = useHydratedRef();
  useScrollToFirstErrorOnSubmit<IssueAttributes>(ref);
  useScrollToAttachmentErrorInCreationForm(ref);
  const { T } = useTranslations();
  const [countKey, incrementCountKey] = useIncrementor();

  const { value: shouldHideEmptyRolesAndCompanies } = useFeatureFlag(
    FeatureFlag.HideEmptyRolesAndCompanies
  );

  const watcherProp = useWatcherPropName();
  const { getWatchersValue } = useGetWatchersValue();
  const selectedWatcherIds =
    watcherProp === "watchers"
      ? values.watchers
      : values.watcherObjects?.map(x => x.id);

  const { maybeShowWatchersNotification } = useMaybeShowWatchersNotification();
  const onWatchersDropdownCloseHandler = useCallback(() => {
    if (watcherProp === "watchers") {
      return;
    }
    const watchers = values[watcherProp];
    if (!watchers) {
      return;
    }
    maybeShowWatchersNotification({ watchers });
  }, [maybeShowWatchersNotification, values, watcherProp]);

  const initialIssueTypeId = useInitialType(fixedTypeId);
  // use useState to ensure it's calculated only once when the component mounts
  const [actualInitialTypeId] = useState(() => values.issueTypeId);
  // Re-apply initial values in case the form is initiated fast before types are loaded
  useEffect(() => {
    if (!actualInitialTypeId && initialIssueTypeId) {
      setFieldValue("issueTypeId", initialIssueTypeId.id);
      setFieldValue("title", initialIssueTypeId.title);
    }
  }, [actualInitialTypeId, initialIssueTypeId, setFieldValue]);

  return (
    // The ref had to be moved to an upper div because it was having some race condition when the
    // template was selected. Some times it would work as expected but others it would cause errors
    // on components with dropdowns that relied on the ref to work. This way we will always have the
    // correct ref and only the child div will re-render when a template is selected
    <div ref={ref}>
      <div key={countKey}>
        <Form>
          {!fixedTypeId && (
            <IssueTemplatesField
              // IssueTemplatesField handles everything about updating formik, and any confirmation
              // modals -- but we need the onTemplateChanged to ensure that the form values
              // in the current form are cleared, and we do that by re-rendering everything by
              // reinstantiating the form by updating the key of its wrapper.
              onTemplateChanged={() => {
                incrementCountKey();
              }}
              shouldIncludeMemberFn={shouldIncludeMemberFn}
            />
          )}
          <FieldWrapper
            required={requireTitleField}
            label={titleFieldLabel || T.translate("issueAttributes.title")}
            errorMessage={touched.title ? errors.title : undefined}
            testId="title-field"
          >
            <TextInput
              name="title"
              autoComplete="off"
              value={values.title}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder={
                T.translate("issueCreation.title.placeholder") as string
              }
              error={touched.title && !!errors.title}
              id="title-field"
              ref={titleInputRef}
            />
          </FieldWrapper>
          {!hideStatusField && (
            <FieldWrapper
              label={T.translate("issueAttributes.status")}
              errorMessage={touched.status ? errors.status : undefined}
              testId="status-field"
            >
              <ConditionalTooltip
                condition={areThereHiddenRequiredStandardFields}
                content={T.translate(
                  "issueCreation.status.requiredFieldDisabledReason"
                )}
              >
                <StatusDropdown
                  status={values.status}
                  statusOptions={permittedStatuses}
                  onChange={(newStatus: IssueStatus) => {
                    const fieldName = "status";
                    setFieldValue(fieldName, newStatus);
                    setFieldTouched(fieldName, true, false);
                  }}
                  data-testid="create-issue-modal-status"
                  disabled={areThereHiddenRequiredStandardFields}
                />
              </ConditionalTooltip>
            </FieldWrapper>
          )}
          {fixedTypeId ? (
            <FixedTypeField
              typeId={fixedTypeId}
              // No touched/submitCount guard: the field is read-only, so the
              // not-permitted error is surfaced as soon as the modal opens.
              errorMessage={errors.issueTypeId}
            />
          ) : (
            <FieldWrapper
              label={T.translate("issueAttributes.issueType")}
              errorMessage={
                touched.issueTypeId ? errors.issueTypeId : undefined
              }
              testId="type-field"
            >
              <TypeDropdown
                selectedTypeId={values.issueTypeId}
                onChange={typeId => {
                  const fieldName = "issueTypeId";
                  setFieldValue(fieldName, typeId);
                  setFieldTouched(fieldName, true, false);
                  setLastSelectedTypeId(typeId);
                  clearObsoleteCustomFields(typeId);
                }}
                data-testid="create-issue-modal-type"
              />
            </FieldWrapper>
          )}

          {issueFields.map((field: FieldMetadata) => {
            const fieldId = field.id;
            const isFieldRequired = !!field.required;
            if (!field.visible) {
              // do not show fields that should not be visible!
              return null;
            }
            if (fieldId === IssueStandardFieldIds.Description) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.description")}
                  errorMessage={
                    touched.description || submitCount > 0
                      ? errors.description
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="description-field"
                >
                  <Textarea
                    style={{ width: "100%" }}
                    // @ts-ignore https://jira.autodesk.com/browse/DSGN-661
                    name="description"
                    autoComplete="off"
                    value={values.description}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    maxHeight={70}
                    minHeight={70}
                    placeholder={
                      T.translate(
                        "issueCreation.description.placeholder"
                      ) as string
                    }
                    error={
                      (touched.description || submitCount > 0) &&
                      !!errors.description
                    }
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.Assignee) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.assignedTo")}
                  errorMessage={
                    touched.assignedTo || submitCount > 0
                      ? errors.assignedTo
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="assignee-field"
                >
                  <AssigneeDropdown
                    selectedAssigneeId={values.assignedTo}
                    selectedWatchers={values.watcherObjects}
                    onChange={({ assignedTo, assignedToType, watchers }) => {
                      setFieldValue("assignedTo", assignedTo);
                      setFieldValue("assignedToType", assignedToType);
                      if (watchers) {
                        setFieldValue(watcherProp, watchers);
                      }
                    }}
                    shouldIncludeMemberFn={shouldIncludeMemberFn}
                    shouldIncludeEmpty={!shouldHideEmptyRolesAndCompanies}
                    clearable={permittedActions.includes(
                      PermittedAction.ClearAssignee
                    )}
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.Watchers) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={<WatcherFieldLabel />}
                  errorMessage={
                    touched[watcherProp] || submitCount > 0
                      ? errors[watcherProp]
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="watchers-field"
                >
                  <WatcherDropdown
                    shouldIncludeMemberFn={shouldIncludeMemberFn}
                    selectedWatcherIds={selectedWatcherIds}
                    onChange={(newSelectedWatcherObjects: WatcherObject[]) => {
                      setFieldValue(
                        watcherProp,
                        getWatchersValue(newSelectedWatcherObjects)
                      );
                    }}
                    onClose={onWatchersDropdownCloseHandler}
                    invalid={
                      (touched[watcherProp] || submitCount > 0) &&
                      !!errors[watcherProp]
                    }
                    shouldIncludeEmpty={false}
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.Location) {
              return (
                <LocationCreateField
                  key={fieldId}
                  isRequired={isFieldRequired}
                  errorMessage={
                    touched.locationId || submitCount > 0
                      ? errors.locationId
                      : undefined
                  }
                />
              );
            } else if (fieldId === IssueStandardFieldIds.LocationDetails) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.locationDetails")}
                  errorMessage={
                    touched.locationDetails || submitCount > 0
                      ? errors.locationDetails
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="location-details-field"
                >
                  <TextInput
                    name="locationDetails"
                    autoComplete="off"
                    value={values.locationDetails}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder={
                      T.translate(
                        "issueCreation.locationDetails.placeholder"
                      ) as string
                    }
                    error={
                      (touched.locationDetails || submitCount > 0) &&
                      !!errors.locationDetails
                    }
                    data-testid="create-issue-modal-locationdetails"
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.GpsCoordinates) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.gpsCoordinates")}
                  errorMessage={
                    touched.gpsCoordinates || submitCount > 0
                      ? errors.gpsCoordinates
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="geo-location-field"
                >
                  <GeoLocationPickerField
                    onChange={(gpsCoordinates: GpsCoordinates) => {
                      setFieldValue("gpsCoordinates", gpsCoordinates);
                    }}
                    error={
                      (touched.gpsCoordinates || submitCount > 0) &&
                      !!errors.gpsCoordinates
                    }
                    htmlId={fieldId}
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.DueDate) {
              return (
                <FieldWrapper
                  testId={"duedate-field"}
                  key={fieldId}
                  label={T.translate("issueAttributes.dueDate")}
                  errorMessage={
                    touched.dueDate || submitCount > 0
                      ? errors.dueDate
                      : undefined
                  }
                  required={isFieldRequired}
                >
                  <DateField
                    date={values.dueDate}
                    onChange={newDate => setFieldValue("dueDate", newDate)}
                    data-testid={"create-issue-modal-duedate"}
                    error={
                      (touched.dueDate || submitCount > 0) && !!errors.dueDate
                    }
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.StartDate) {
              return (
                <FieldWrapper
                  testId={"startdate-field"}
                  key={fieldId}
                  label={T.translate("issueAttributes.startDate")}
                  errorMessage={
                    touched.startDate || submitCount > 0
                      ? errors.startDate
                      : undefined
                  }
                  required={isFieldRequired}
                >
                  <DateField
                    date={values.startDate}
                    onChange={newDate => setFieldValue("startDate", newDate)}
                    data-testid={"create-issue-modal-startdate"}
                    error={
                      (touched.startDate || submitCount > 0) &&
                      !!errors.startDate
                    }
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.Placement) {
              return (
                <FieldWrapper
                  testId="placement-field"
                  key={fieldId}
                  label={T.translate("issueAttributes.placement")}
                >
                  <PlacementDropdown
                    onChange={(placements, option) => {
                      const { snapshotBlob, locationId } = option;
                      if (!placements) {
                        setFieldValue("placements", undefined);
                        setFieldValue("snapshotBlob", undefined);
                        return;
                      }

                      setFieldValue("placements", placements);

                      if (locationId) {
                        setFieldValue("locationId", locationId);
                      }
                      if (snapshotBlob) {
                        setFieldValue("snapshotBlob", snapshotBlob);
                      }
                    }}
                    error={!!errors.placements}
                    issueType={selectedType}
                  />
                </FieldWrapper>
              );
            } else if (
              fieldId === IssueStandardFieldIds.RootCauseWithCategory
            ) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.rootCause")}
                  errorMessage={
                    touched.rootCauseId || submitCount > 0
                      ? errors.rootCauseId
                      : undefined
                  }
                  required={isFieldRequired}
                  testId="root-cause-field"
                >
                  <RootCauseDropdown
                    selectedRootCauseId={values.rootCauseId}
                    onChange={({ rootCauseId }) =>
                      setFieldValue("rootCauseId", rootCauseId)
                    }
                    data-testid={"create-issue-modal-rootcause"}
                    invalid={
                      (touched.rootCauseId || submitCount > 0) &&
                      !!errors.rootCauseId
                    }
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.Attachments) {
              return (
                <FieldWrapper
                  key={fieldId}
                  label={T.translate("issueAttributes.attachments")}
                  required={isFieldRequired}
                  errorMessage={errors.attachments}
                >
                  <AttachmentsField
                    isDeletedIssue={false}
                    issue={null}
                    onChange={draftAttachments => {
                      setFieldValue("attachments", draftAttachments);
                    }}
                    error={!!errors.attachments}
                  />
                </FieldWrapper>
              );
            } else if (fieldId === IssueStandardFieldIds.References) {
              // no references field yet in creation modal
              return null;
            } else if (
              field.type === "custom" &&
              mappingsByCustomFieldId[field.id]
            ) {
              const mapping = mappingsByCustomFieldId[field.id];

              return (
                <CustomFieldWithFormikContext
                  {...mapping}
                  key={fieldId}
                  isRequired={isFieldRequired}
                />
              );
            }
          })}
        </Form>
      </div>
    </div>
  );
};
